'use client';
import { createContext, useContext, useState, useEffect, FC, ReactNode } from 'react';
import { detectBrowserLanguage, getLanguage, saveLanguage } from '../helpers/appStorageMethod';

interface LanguageContextType {
    language: string;
    setLanguage: (language: string) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface ILanguageProvider {
    children: ReactNode;
}

export const LanguageProvider: FC<ILanguageProvider> = ({ children }) => {
    // 서버(SSR)에는 navigator 도 저장소도 없으므로 여기서는 'ko' 로 두고,
    // 실제 언어는 아래 useEffect 가 브라우저에서 정한다. isLoading 동안 children 을
    // 그리지 않으므로 이 초기값이 화면에 보이지는 않는다.
    const [language, setLanguageState] = useState<string>('ko');
    const [isLoading, setIsLoading] = useState<boolean>(true);

    // Load language from storage on mount
    useEffect(() => {
        const loadLanguage = async () => {
            try {
                // getLanguage 는 저장된 선택이 있으면 그것을, 없으면 브라우저 언어(ko/en/ja)를 준다.
                const savedLanguage = await getLanguage();
                setLanguageState(savedLanguage);
            } catch (error) {
                console.error('Failed to load language:', error);
                // 저장소를 못 읽은 것뿐이므로 한국어로 못박지 말고 브라우저 언어라도 살린다.
                setLanguageState(detectBrowserLanguage());
            } finally {
                setIsLoading(false);
            }
        };

        loadLanguage();
    }, []);

    const setLanguage = async (newLanguage: string) => {
        try {
            setLanguageState(newLanguage);
            await saveLanguage(newLanguage);
        } catch (error) {
            console.error('Failed to save language:', error);
        }
    };

    return (
        <LanguageContext.Provider value={{ language, setLanguage }}>
            {!isLoading && children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = (): LanguageContextType => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
